import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { EyeIcon } from "lucide-react";
import { observer } from "mobx-react-lite";

import { AppModel } from "../../../app/app-model";
import { useCountries } from "../../../catalogs/countries";
import { AButton } from "../../../components/ui-kit/a-button";
import { AddButton } from "../../../components/ui-kit/add-button";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import {
  monitoringTypes,
  supportedLanguages,
} from "../../../constants/enum-to-text";
import {
  getCountryOptions,
  getRegionOptions,
  regionDisabled,
  regionPredicate,
} from "../../../utils/filter-presets";
import { assert } from "../../../utils/utils";

export const ConsentsRoute = observer(function ConsentsRoute(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const admin = props.app.requireAdminShit();
  const model = admin.studyTexts;
  assert(model, "Study texts model not initialized");
  const countries = useCountries();

  return (
    <>
      <DataTable
        persistState
        itemKeyField="uuid"
        filterNoun={(count) =>
          t`${plural(count, { one: "study text", other: "study texts" })}`
        }
        filterActions={
          <AddButton href="add">
            <Trans>Add consent</Trans>
          </AddButton>
        }
        columns={[
          {
            header: <Trans>Monitoring Type</Trans>,
            attr: "monitoring_type",
            type: "enum",
            values: monitoringTypes,
            filter: {
              kind: "multi",
              group: t`Consent`,
              options: new Map(Object.entries(monitoringTypes)),
            },
            cell: ({ item }) => item.monitoringTypeText,
          },
          {
            header: <Trans>Language</Trans>,
            attr: "study_text_language",
            type: "enum",
            values: supportedLanguages,
            filter: {
              kind: "multi",
              group: t`Consent`,
              options: new Map(Object.entries(supportedLanguages)),
            },
            cell: ({ item }) => item.languageText,
          },
          {
            header: <Trans>Country</Trans>,
            attr: "country",
            type: "enum",
            values: countries.countriesEnum,
            filter: {
              kind: "multi",
              group: t`Location`,
              options: getCountryOptions,
            },
            cell: ({ item }) => item.country,
          },
          {
            header: <Trans>Region</Trans>,
            attr: "region",
            filter: {
              kind: "multi",
              group: t`Location`,
              options: (table) => getRegionOptions(table, countries),
              disabled: regionDisabled,
              disabledMessage: t`Select a country first`,
              predicate: regionPredicate,
            },
            cell: ({ item }) => {
              const regions = item.country
                ? countries.getRegionsEnum(item.country)
                : undefined;
              return regions?.[item.region as any] ?? item.region;
            },
          },
          {
            header: <Trans>Clinic</Trans>,
            cell: ({ item }) => item.clinicName,
          },
          {
            header: <Trans>Physician</Trans>,
            cell: ({ item }) => item.physicianName,
          },
          {
            header: <Trans>View</Trans>,
            sticky: "right",
            width: 60,
            cell: ({ item }) => (
              <AButton
                href={`/admin/settings/consents/${item.uuid}`}
                variant="outline"
                size="sm"
              >
                <EyeIcon className="text-foreground" size={12} />
              </AButton>
            ),
          },
        ]}
        model={model}
        rowsPerPage={10}
      />
    </>
  );
});
